import { useCallback, useEffect, useState, useRef } from 'react';

// import { Modal } from './Modal';
import { netlifyStore } from '~/lib/stores/netlify';
import { useNetlifyLogs } from '~/lib/hooks/useNetlifyLogs';
import { useNetlifyAuth } from '~/lib/hooks/useNetlifyAuth';
import { useNetlifyDeployment } from '~/lib/hooks/useNetlifyDeployment';
import { useNetlifyStatus } from '~/lib/hooks/useNetlifyStatus';
import * as Sentry from '@sentry/remix';
import { useTranslation } from '~/lib/hooks/useTranslation';
import {
  AuthSection,
  UserProfile,
  DeployConfirmation,
  DeploymentProgress,
  NetlifySuccessDisplay,
  ErrorDisplay,
  LinkButtons,
} from './netlify';
import { setDeploymentStatus } from '~/lib/stores/netlify';
import { updateIndexHtml, updateEnvFile } from '~/lib/utils/netlify-helpers';
import { projectId, db } from '~/lib/persistence/useChatHistory';
import { useStore } from '@nanostores/react';
import { selectionStore } from '~/lib/persistence/selectionStore';
import { usePosthogCommonEvents } from '~/lib/hooks/posthog/usePosthogCommonEvents';
import { usePosthogEvent } from '~/lib/hooks/posthog/usePosthogEvent';
import type { DeployModalProps } from '~/lib/types/netlify';

const FIX_WITH_DUALITE_SOURCE_DEPLOY = 'deploy_error';

const DeployModal = ({ isOpen, onClose, netlifyConfig }: DeployModalProps) => {
  const { t, ready } = useTranslation(false);
  const { trackTemplateEvent } = usePosthogCommonEvents();
  const { trackEvent } = usePosthogEvent();
  const hasTrackedDeployErrorShownRef = useRef(false);

  // State to solve the circular dependency
  const [logsCleanupFn, setLogsCleanupFn] = useState<(() => void) | undefined>(undefined);

  const [showRedeployMessage, setShowRedeployMessage] = useState(false);
  const [isBackgroundRedeploy, setIsBackgroundRedeploy] = useState(false);

  // Ref to track connection timeout
  const connectTimeoutRef = useRef<number | null>(null);

  const {
    accessToken,
    user,
    isValidatingToken,
    isCheckingToken,
    tokenCheckCount,
    validateToken,
    handleDisconnect,
    isDisconnecting,
    initiateOAuthFlow,
    resetAuthState,
  } = useNetlifyAuth(netlifyConfig, logsCleanupFn);

  // Use store for deployedId to avoid state duplication
  const storeState = netlifyStore.get();
  const screenshotUrl = useStore(netlifyStore).screenshotUrl;
  const deployedId = storeState.deployedId;

  const { logs, isConnecting, connectToLogs, safeConnectToLogs, getLogsForErrorFix, cleanupLogs } = useNetlifyLogs(
    deployedId,
    accessToken,
  );

  // Store the cleanupLogs function in state for the auth hook to use
  useEffect(() => {
    console.log('[DeployModal] Effect to store the cleanupLogs function in state for the auth hook to use');
    setLogsCleanupFn(() => cleanupLogs);
  }, [cleanupLogs]);

  const {
    // These are now all from global store, no more local state
    confirmDeploy,
    startDeployment,
    checkExistingDeployment,
    handleRedeploy,
    setConfirmDeploy,
  } = useNetlifyDeployment(accessToken, cleanupLogs);

  const { deploymentStatus, deployedUrl, error, updateDeploymentStatus, getCurrentStep, isErrorFixable } =
    useNetlifyStatus(deployedId, accessToken);

  // Effect to check for existing deployment when modal opens
  useEffect(() => {
    console.log('[DeployModal] Effect to check for existing deployment when modal opens');

    if (!isOpen) {
      return;
    }

    checkExistingDeployment();
  }, [isOpen]);

  // Track when "Fix with Dualite" is shown in deploy modal (deployment error state)
  useEffect(() => {
    if (!isOpen) {
      hasTrackedDeployErrorShownRef.current = false;
      return;
    }

    if (deploymentStatus === 'error' && isErrorFixable && !hasTrackedDeployErrorShownRef.current) {
      hasTrackedDeployErrorShownRef.current = true;

      try {
        trackEvent({
          name: 'fix_with_dualite_shown',
          properties: { source: FIX_WITH_DUALITE_SOURCE_DEPLOY },
        });
      } catch (e) {
        console.error('[PostHog] fix_with_dualite_shown:', e);
      }
    }
  }, [isOpen, deploymentStatus, isErrorFixable, trackEvent]);

  // Effect to handle deployment when confirmDeploy is true
  useEffect(() => {
    console.log('[DeployModal] Effect to handle deployment when confirmDeploy is true', {
      isOpen,
      accessToken,
      confirmDeploy,
    });

    if (!isOpen || !accessToken || !confirmDeploy) {
      return;
    }

    const handleDeploy = async () => {
      try {
        const isValid = await validateToken();

        if (!isValid) {
          return;
        }

        await startDeployment();
      } catch (err) {
        Sentry.captureException(err, {
          extra: {
            message: 'Deployment error',
            accessToken,
            confirmDeploy,
          },
        });
        console.error('Deployment error:', err);
      }
    };

    handleDeploy();

    setConfirmDeploy(false);
  }, [isOpen, confirmDeploy, accessToken]);

  // Automatically connect to logs when deployedId is set
  useEffect(() => {
    if (deployedId && accessToken && deploymentStatus === 'building') {
      console.log('Auto-connecting to logs after deployedId update.', {
        deployedId,
        accessToken,
        deploymentStatus,
      });

      // Clear any previous connect timeout
      if (connectTimeoutRef.current) {
        console.log('[DeployModal] Clearing previous connect timeout');
        clearTimeout(connectTimeoutRef.current);
      }

      // Debounce the connection to avoid multiple rapid connects
      connectTimeoutRef.current = window.setTimeout(() => {
        console.log('[DeployModal] Connecting to logs after timeout');

        safeConnectToLogs();
        connectTimeoutRef.current = null;
      }, 300);
    }

    // Clean up on unmount
    return () => {
      if (connectTimeoutRef.current) {
        console.log('[DeployModal] Cleaning up connect timeout');
        clearTimeout(connectTimeoutRef.current);
      }
    };
  }, [deployedId, accessToken, deploymentStatus, safeConnectToLogs]);

  // Process logs to update deployment status
  useEffect(() => {
    console.log('[DeployModal] Effect to process logs to update deployment status', {
      logs,
      deploymentStatus,
    });

    // Loop through logs only once
    for (const log of logs) {
      // Update to building step when section is "building"
      if (log.type === 'line' || log.type === 'start') {
        const currentStatus = netlifyStore.get().deploymentStatus;

        if (log.section === 'building' && currentStatus === 'new') {
          console.log('Deployment is now in building phase');
          setDeploymentStatus('building');
        }

        // Update to deploying step when section is "deploying"
        if (log.section === 'deploying' && currentStatus === 'building') {
          // We keep the status as 'building' in the store but update the UI step
          console.log('Deployment is now in deploying phase');
        }
      } else if (log.type === 'report') {
        console.log('[DeployModal] Updating deployment status to deploying');
        updateDeploymentStatus();
      } else {
        if (log.type !== 'info') {
          console.info('Log type not recognized:', log.type);
        }
      }
    }
  }, [logs, updateDeploymentStatus]);

  // seo optimization post deployment
  const hasRun = useRef(false);
  const hasTrackedPublish = useRef(false);

  useEffect(() => {
    const { hasExistingDeployment } = netlifyStore.get();

    if (deploymentStatus === 'ready' && deployedUrl && !hasRun.current && !hasExistingDeployment) {
      // TODO add a screenshot fallback
      setShowRedeployMessage(true);
      setIsBackgroundRedeploy(true);
      updateIndexHtml(db, projectId.get(), screenshotUrl || 'https://ibb.co/67X3xfSV', deployedUrl).then(() => {
        handleRedeploy();
        hasRun.current = true;
      });

      const projectSource = selectionStore.get().projectSource;

      if (projectSource === 'alpha_template') {
        updateEnvFile(db, projectId.get(), deployedUrl);

        if (!hasTrackedPublish.current) {
          hasTrackedPublish.current = true;
          trackTemplateEvent('published', {
            project_id: projectId.get() ?? undefined,
            template_id: selectionStore.get().templateId,
            template_name: selectionStore.get().templateName,
            deploy_target: 'netlify',
          });
        }
      }
    }
  }, [deploymentStatus, deployedUrl, screenshotUrl, trackTemplateEvent]);

  // When background redeploy finishes, hide the SEO message and reset background redeploy state
  useEffect(() => {
    if (isBackgroundRedeploy && deploymentStatus === 'ready' && hasRun.current) {
      setShowRedeployMessage(false);
      setIsBackgroundRedeploy(false);
    }
  }, [deploymentStatus, isBackgroundRedeploy]);

  // Handler for the "Fix with Dualite" button
  const handleFixWithDualite = useCallback(async () => {
    if (!error) {
      return;
    }

    try {
      trackEvent({
        name: 'fix_with_dualite_clicked',
        properties: { source: FIX_WITH_DUALITE_SOURCE_DEPLOY },
      });
    } catch (e) {
      console.error('[PostHog] fix_with_dualite_clicked:', e);
    }

    // Show loading state
    const button = document.querySelector('#fix-with-dualite-button');

    if (button) {
      button.innerHTML = `<div class="i-ph:circle-notch w-4 h-4 animate-spin mr-2"></div>${t('modals.deploy.collectingLogs')}`;
    }

    // If we don't have logs yet, connect and wait briefly
    if (logs.length === 0 && !isConnecting) {
      console.log('[DeployModal] No logs available for error fix, connecting to logs');
      safeConnectToLogs();

      // Wait for logs to be fetched
      await new Promise((resolve) => setTimeout(resolve, 1500));
    } else {
      console.log('[DeployModal] Logs already available or connecting for error fix');
    }

    const buildingLogs = logs.filter((log) => log.section === 'building').map((log) => log.message);
    const initializingLogs = logs.filter((log) => log.section === 'initializing').map((log) => log.message);

    // Format logs for the prompt - only use initializing and building section logs
    const logsText =
      buildingLogs.length > 0
        ? `\nDeployment Logs (building section):\n ${initializingLogs.join('\n')} \n ${buildingLogs.join('\n')}`
        : '\nNo relevant deployment logs available.';

    // Create prompt and dispatch event
    const errorPrompt = `I'm encountering the following error when deploying to Netlify:
    
Error: ${error}
${logsText}

Please analyze this error and the deployment logs to provide the necessary code fixes to address it. Focus on fixing the deployment issue.`;

    const fixEvent = new CustomEvent('dualite-fix-error', {
      detail: { prompt: errorPrompt, errorType: 'GENERAL_ERROR' },
    });

    document.dispatchEvent(fixEvent);

    console.log('Dispatched error fix event for Netlify deployment error with filtered logs');

    // Close the modal
    onClose();
  }, [error, onClose, logs, isConnecting, safeConnectToLogs, getLogsForErrorFix, t, trackEvent]);

  const handleAbortConnection = async () => {
    localStorage.removeItem('netlifyState');
    await handleDisconnect();
    resetAuthState();

    const netlifyWindow = window.open('', 'netlify-auth');

    if (netlifyWindow) {
      netlifyWindow.close();
    }
  };

  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  if (!ready) {
    return null;
  }

  return (
    <div ref={modalRef} className="w-[340px] relative animate-in fade-in zoom-in-95 duration-200">
      <div className="absolute -top-1.5 right-6 w-3 h-3 bg-dualite-background-tertiary/80 rotate-45 border-l border-t border-dualite-border-primary" />
      <div className="bg-dualite-background-tertiary rounded-xl shadow-md dark:shadow-black/70 flex flex-col overflow-hidden border border-dualite-border-primary overflow-x-hidden overflow-y-auto">
        <div className="p-4 max-h-[80vh]">
          <div className="flex justify-between items-start mb-2">
            <div className="flex flex-col items-start">
              <h2 className="text-md font-semibold text-dualite-text-primary mb-2">{t('modals.deploy.title')}</h2>
              {!accessToken && (
                <p className="text-xs text-dualite-text-secondary py-3">{t('modals.deploy.notConnected')}</p>
              )}
            </div>
            {/* <button
              onClick={onClose}
              className="text-[#e7e7e7] hover:text-white bg-transparent 
                border-none rounded-md hover:bg-white/10 transition-colors"
            >
              <div className="i-ph:x w-4 h-4" />
            </button> */}
          </div>

          {accessToken && user && (
            <UserProfile user={user} onDisconnect={handleDisconnect} isDisconnecting={isDisconnecting} />
          )}

          {accessToken && isValidatingToken && (
            <div className="flex items-center justify-center gap-2 mt-3 text-sm text-dualite-text-secondary">
              <div className="i-ph:circle-notch w-4 h-4 animate-spin" />
              <span>{t('modals.deploy.validatingToken')}</span>
            </div>
          )}

          <div className="space-y-6">
            {!accessToken ? (
              <AuthSection
                isCheckingToken={isCheckingToken}
                tokenCheckCount={tokenCheckCount}
                onConnect={initiateOAuthFlow}
                onAbort={handleAbortConnection}
              />
            ) : (
              <div className="space-y-4">
                {/* During background redeploy, always show the completed deployment UI */}
                {isBackgroundRedeploy ? (
                  deployedUrl && (
                    <>
                      <NetlifySuccessDisplay
                        adminUrl={storeState.adminUrl}
                        deployedId={deployedId}
                        accessToken={accessToken}
                        deployStatus={deploymentStatus}
                        showRedeployMessage={showRedeployMessage}
                      />
                    </>
                  )
                ) : (
                  <>
                    {deploymentStatus === 'ready' && deployedUrl && (
                      <NetlifySuccessDisplay
                        adminUrl={storeState.adminUrl}
                        deployedId={deployedId}
                        accessToken={accessToken}
                        deployStatus={deploymentStatus}
                      />
                    )}
                    {(deploymentStatus === 'new' || deploymentStatus === 'building') && (
                      <DeploymentProgress
                        currentStep={getCurrentStep()}
                        deployedId={deployedId}
                        accessToken={accessToken}
                        deployStatus={deploymentStatus}
                        adminUrl={storeState.adminUrl}
                      />
                    )}
                  </>
                )}

                {(isBackgroundRedeploy || deploymentStatus === 'ready') && deployedUrl && storeState.adminUrl && (
                  <LinkButtons
                    deployedUrl={deployedUrl}
                    adminUrl={storeState.adminUrl}
                    deployedId={deployedId ?? ''}
                    accessToken={accessToken}
                    deployStatus={deploymentStatus}
                  />
                )}

                {!confirmDeploy && (
                  <DeployConfirmation
                    deployedId={deployedId}
                    accessToken={accessToken}
                    hasExistingDeployment={storeState.hasExistingDeployment}
                    previousDeployUrl={storeState.previousDeployUrl}
                    adminUrl={storeState.adminUrl}
                    deploymentStatus={deploymentStatus}
                    onRedeploy={handleRedeploy}
                    showPushSection={isBackgroundRedeploy || storeState.hasExistingDeployment}
                    isBackgroundRedeploy={isBackgroundRedeploy}
                  />
                )}

                {deploymentStatus === 'error' && (
                  <ErrorDisplay
                    isErrorFixable={isErrorFixable}
                    error={error}
                    adminUrl={storeState.preAdminUrl}
                    onFixWithDualite={handleFixWithDualite}
                    deployedId={deployedId}
                    accessToken={accessToken}
                    deployStatus={deploymentStatus}
                    hasExistingDeployment={storeState.hasExistingDeployment}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeployModal;
